<template>
  <div>
    <b-alert v-if="!canRead" show variant="info">{{
      $t("label.eventAccessDenied")
    }}</b-alert>
    <template v-else>
      <h6 v-if="eventName" class="mb-3">{{ eventName }}</h6>
      <div class="row mb-3">
        <div class="col-md-7 mb-2 mb-md-0">
          <b-input-group>
            <b-form-input
              v-model.trim="searchText"
              type="search"
              :placeholder="$t('label.search')"
              @keyup.enter="applyFilters"
            />
            <b-input-group-append
              ><b-button
                variant="info"
                :aria-label="$t('label.search')"
                @click="applyFilters"
                ><i class="mdi mdi-magnify"></i></b-button
            ></b-input-group-append>
          </b-input-group>
        </div>
        <div class="col-md-5">
          <b-form-select
            v-model="statusFilter"
            :options="statusOptions"
            :aria-label="$t('label.status')"
            @change="applyFilters"
          />
        </div>
      </div>
      <div v-if="loading" class="text-center p-5">
        <b-spinner variant="info" />
      </div>
      <b-alert v-else-if="loadError" show variant="danger"
        >{{ $t("label.eventUsersLoadError") }}
        <b-button variant="outline-danger" size="sm" @click="loadUsers">{{
          $t("label.retryPermissionDefinitions")
        }}</b-button></b-alert
      >
      <template v-else>
        <b-table
          :items="users"
          :fields="fields"
          primary-key="uuid"
          responsive
          show-empty
          no-local-sorting
          sort-by="createdAt"
          :sort-desc="criteria.orderBy === 'desc'"
          @sort-changed="changeSort"
        >
          <template #cell(name)="{ item }"
            ><div class="d-flex align-items-center">
              <img
                v-if="item.image"
                :src="item.image"
                alt=""
                class="event-user-avatar mr-2"
              /><span>{{ item.name }}</span>
            </div></template
          >
          <template #cell(status)="{ item }"
            ><b-badge :variant="statusVariant(item.status)">{{
              statusName(item.status)
            }}</b-badge></template
          >
        </b-table>
        <div
          v-if="pagination.total"
          class="d-flex flex-wrap justify-content-between align-items-center mt-3"
        >
          <small class="text-muted">{{
            $tc("label.results", pagination.total, [pagination.total])
          }}</small>
          <b-pagination
            :value="criteria.pageNumber"
            :total-rows="pagination.total"
            :per-page="pagination.pageSize || criteria.pageSize"
            class="mb-0"
            @change="changePage"
          />
        </div>
        <small class="text-muted d-block mt-2">{{
          $t("label.eventTimezone")
        }}</small>
      </template>
    </template>
  </div>
</template>
<script>
import { eventService } from "@/services/api/event.service";
import { eventUserModel } from "@/models/api/eventUser.model";
import { sessionHelper, sharedHelper } from "@/utils";
const defaultCriteria = () => ({
  pageNumber: 1,
  pageSize: 15,
  status: 1,
  searchArray: [],
  sortBy: "created_at",
  orderBy: "desc",
});
const statusKeys = [
  "eventUserWaiting",
  "eventUserJoined",
  "eventUserCancelled",
  "eventUserPrize",
  "eventUserAttended",
];
export default {
  name: "EventUsers",
  props: {
    eventUUID: { type: String, required: true },
    eventName: { type: String, default: "" },
  },
  data() {
    return {
      criteria: defaultCriteria(),
      searchText: "",
      statusFilter: 1,
      users: [],
      pagination: { total: 0 },
      loading: true,
      loadError: false,
      requestId: 0,
    };
  },
  computed: {
    canRead() {
      const user = this.$store.state.api.auth.user?.user;
      return (
        (sessionHelper.getUserPermissions(user?.permissions, user?.isSuperadmin)
          .event_admin || 0) >= 1
      );
    },
    statusOptions() {
      return [
        { value: "", text: this.$t("label.all") },
        ...statusKeys.map((key, value) => ({
          value,
          text: this.$t(`label.${key}`),
        })),
      ];
    },
    fields() {
      return [
        { key: "name", label: this.$t("label.name"), class: "event-user-name" },
        {
          key: "email",
          label: this.$t("label.email"),
          class: "event-user-email",
        },
        { key: "phone", label: this.$t("label.phone"), class: "text-nowrap" },
        { key: "status", label: this.$t("label.status"), class: "text-nowrap" },
        {
          key: "createdAt",
          label: this.$t("label.eventUserRegisteredAt"),
          sortable: true,
          class: "text-nowrap",
        },
      ];
    },
  },
  watch: {
    eventUUID() {
      this.criteria = defaultCriteria();
      this.searchText = "";
      this.statusFilter = 1;
      this.users = [];
      this.pagination = { total: 0 };
      this.loadUsers();
    },
    canRead() {
      this.loadUsers();
    },
  },
  created() {
    this.loadUsers();
  },
  beforeDestroy() {
    this.requestId++;
  },
  methods: {
    async loadUsers() {
      const requestId = ++this.requestId;
      if (
        !this.canRead ||
        !this.eventUUID ||
        sessionHelper.getPermissionLevel("event_admin") < 1
      ) {
        this.users = [];
        this.pagination = { total: 0 };
        this.loading = false;
        this.loadError = false;
        return;
      }
      this.loading = true;
      this.loadError = false;
      try {
        const result = await eventService.getUsers(
          this.eventUUID,
          this.criteria,
        );
        if (!result?.success || !Array.isArray(result.data))
          throw new Error("Invalid event users response.");
        if (requestId !== this.requestId) return;
        this.users = result.data.map(eventUserModel);
        this.pagination = result.pagination
          ? sharedHelper.apiPaginationAdapter(result.pagination)
          : { total: result.data.length, pageSize: this.criteria.pageSize };
      } catch (error) {
        if (requestId === this.requestId) this.loadError = true;
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    applyFilters() {
      this.criteria = {
        ...this.criteria,
        pageNumber: 1,
        status: this.statusFilter,
        searchArray: this.searchText ? [this.searchText] : [],
      };
      return this.loadUsers();
    },
    changePage(pageNumber) {
      this.criteria = { ...this.criteria, pageNumber };
      return this.loadUsers();
    },
    changeSort({ sortBy, sortDesc }) {
      if (sortBy !== "createdAt") return;
      const orderBy = sortDesc ? "desc" : "asc";
      if (this.criteria.orderBy === orderBy) return;
      this.criteria = {
        ...this.criteria,
        pageNumber: 1,
        sortBy: "created_at",
        orderBy,
      };
      return this.loadUsers();
    },
    statusName(status) {
      return [0, 1, 2, 3, 4].includes(status)
        ? this.$t(`label.${statusKeys[status]}`)
        : "—";
    },
    statusVariant(status) {
      return (
        ["warning", "success", "secondary", "info", "primary"][status] ||
        "secondary"
      );
    },
  },
};
</script>
<style scoped>
.event-user-avatar {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 50%;
}
::v-deep .event-user-name {
  min-width: 160px;
  max-width: 280px;
  overflow-wrap: anywhere;
}
::v-deep .event-user-email {
  max-width: 300px;
  overflow-wrap: anywhere;
}
</style>
